import '../css/main.css';

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initBackToTop();
  initLegalScrollspy();
  initMetrics();
  initAccordions();
  initCarousels();
  initContactForm();
  initLanguageSwitcher();
});

function initHeader() {
  const header = document.querySelector('[data-component="header"]');
  const toggle = header?.querySelector('.site-header__toggle');
  const nav = header?.querySelector('.site-header__nav');

  if (!header || !toggle || !nav) {
    return;
  }

  const navLinks = Array.from(nav.querySelectorAll('a'));
  const mobileMq = window.matchMedia('(max-width: 960px)');
  let isMobile = mobileMq.matches;

  const setLinkTabState = (disable) => {
    navLinks.forEach((link) => {
      if (disable) {
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('tabindex');
      }
    });
  };

  const applyNavState = (expanded) => {
    toggle.setAttribute('aria-expanded', String(expanded));
    nav.dataset.expanded = String(expanded);
    if (isMobile) {
      setLinkTabState(!expanded);
    } else {
      setLinkTabState(false);
    }
  };

  const syncNavForViewport = () => {
    isMobile = mobileMq.matches;
    if (isMobile) {
      applyNavState(false);
    } else {
      applyNavState(true);
    }
  };

  syncNavForViewport();
  if (typeof mobileMq.addEventListener === 'function') {
    mobileMq.addEventListener('change', syncNavForViewport);
  } else if (typeof mobileMq.addListener === 'function') {
    mobileMq.addListener(syncNavForViewport);
  }

  toggle.addEventListener('click', () => {
    if (!isMobile) {
      return;
    }
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    applyNavState(!isExpanded);
  });

  // Close on nav link click (especially on mobile)
  nav.addEventListener('click', (e) => {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('#')) {
      // Smooth scroll to in-page section
      const id = href.slice(1);
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    if (isMobile) {
      applyNavState(false);
    }
  });

  header.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      applyNavState(false);
      toggle.focus();
    }
  });

  window.addEventListener('scroll', () => {
    if (window.scrollY > 80) {
      header.classList.add('site-header--scrolled');
    } else {
      header.classList.remove('site-header--scrolled');
    }
  }, { passive: true });
}

function initLegalScrollspy() {
  const toc = document.querySelector('.legal__toc');
  const sections = Array.from(document.querySelectorAll('.legal__block[id]'));
  if (!toc || !sections.length) {
    return;
  }

  const links = Array.from(toc.querySelectorAll('a[href^="#"]'));
  if (!links.length) {
    return;
  }

  const linkById = new Map();
  links.forEach((a) => {
    try {
      const id = decodeURIComponent((a.getAttribute('href') || '').replace('#', ''));
      if (id) linkById.set(id, a);
    } catch {
      /* noop */
    }
  });

  let activeId = null;

  const setActive = (id) => {
    if (activeId === id) return;
    activeId = id;
    links.forEach((a) => {
      const isActive = decodeURIComponent((a.getAttribute('href') || '').replace('#', '')) === id;
      a.classList.toggle('is-active', isActive);
      if (isActive) {
        a.setAttribute('aria-current', 'true');
      } else {
        a.removeAttribute('aria-current');
      }
    });
  };

  // Observe section visibility, prefer the one closest to the top
  const observer = new IntersectionObserver(
    (entries) => {
      // Filter visible entries and pick the one with highest intersection ratio and closest to viewport top
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || b.intersectionRatio - a.intersectionRatio);
      if (visible.length) {
        const id = visible[0].target.id;
        setActive(id);
      }
    },
    {
      root: null,
      // Offset for fixed header; negative top margin means: trigger a bit before top
      rootMargin: '-120px 0px -60% 0px',
      threshold: [0.1, 0.25, 0.5, 0.75, 1.0],
    }
  );

  sections.forEach((sec) => observer.observe(sec));

  // Optional: smooth-scroll behavior is already enabled via CSS (scroll-behavior: smooth)
  // But we prevent focus loss and ensure hash sync without jumping
  toc.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = decodeURIComponent(a.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  });
}

function initBackToTop() {
  const backToTop = document.querySelector('.back-to-top');
  if (!backToTop) {
    return;
  }

  const toggleVisibility = () => {
    if (window.scrollY > 240) {
      backToTop.classList.add('is-visible');
    } else {
      backToTop.classList.remove('is-visible');
    }
  };

  window.addEventListener('scroll', toggleVisibility, { passive: true });
  toggleVisibility();

  backToTop.addEventListener('click', (event) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

function initMetrics() {
  const metrics = document.querySelectorAll('[data-component="metrics"] .metric');
  if (!metrics.length) {
    return;
  }

  const animateMetric = (element) => {
    const valueElement = element.querySelector('.metric__value');
    const target = Number(element.dataset.target || 0);
    if (!valueElement || !Number.isFinite(target) || target <= 0) {
      return;
    }

    const duration = 1600;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOutQuad(progress);
      const currentValue = Math.floor(eased * target);
      valueElement.textContent = currentValue.toLocaleString();

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateMetric(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.45 }
  );

  metrics.forEach((metric) => observer.observe(metric));
}

function initAccordions() {
  const accordions = document.querySelectorAll('[data-accordion] .accordion__item');
  accordions.forEach((item) => {
    const trigger = item.querySelector('.accordion__trigger');
    const panel = item.querySelector('.accordion__panel');
    if (!trigger || !panel) {
      return;
    }

    trigger.addEventListener('click', () => {
      const expanded = item.getAttribute('aria-expanded') === 'true';
      closeAllAccordions(item.parentElement);
      item.setAttribute('aria-expanded', String(!expanded));
    });

    item.setAttribute('aria-expanded', 'false');
  });
}

function closeAllAccordions(container) {
  container?.querySelectorAll('.accordion__item').forEach((item) => {
    item.setAttribute('aria-expanded', 'false');
  });
}

function initCarousels() {
  const carousels = document.querySelectorAll('[data-carousel]');
  carousels.forEach((carousel) => {
    const track = carousel.querySelector('.carousel__track');
    const items = carousel.querySelectorAll('.carousel__item');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');

    if (!track || items.length <= 1) {
      return;
    }

    let index = 0;
    const total = items.length;
    const itemWidth = () => items[0].clientWidth + 32; // gap

    const update = () => {
      track.style.transform = `translateX(${-index * itemWidth()}px)`;
    };

    const move = (direction) => {
      index = (index + direction + total) % total;
      update();
    };

    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));

    let interval = setInterval(() => move(1), 5000);

    carousel.addEventListener('mouseenter', () => clearInterval(interval));
    carousel.addEventListener('mouseleave', () => {
      interval = setInterval(() => move(1), 5000);
    });

    window.addEventListener('resize', update);
    update();
  });
}

function initContactForm() {
  const form = document.querySelector('[data-component="contact-form"]');
  if (!form) {
    return;
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const formData = new FormData(form);
    const name = (formData.get('name') || '').toString().trim();
    const phone = (formData.get('phone') || '').toString().replace(/\D/g, '');
    const email = (formData.get('email') || '').toString().trim();
    const message = (formData.get('message') || '').toString().trim();

    const summary = `New inquiry from ${name || 'Unknown'}%0A%0APhone: ${
      phone || 'N/A'
    }%0AEmail: ${email || 'N/A'}%0A%0AMessage:%0A${message || ''}`;

    const companyPhone = document.body.dataset.whatsapp || '972583100333';
    const url = `https://api.whatsapp.com/send?phone=${companyPhone}&text=${summary}`;
    window.open(url, '_blank', 'noopener');
  });
}

function initLanguageSwitcher() {
  const switcher = document.querySelector('[data-component="lang-switch"]');
  if (!switcher) {
    return;
  }

  const langSegmentPattern = /\/(en|he)(?=\/|$)/;

  switcher.addEventListener('click', () => {
    // Switches the first /en/ or /he/ segment in the path, or adds /en/ for English if no segment is present, if only /en is in beginning it removes it.
    const path = window.location.pathname || '/';
    const search = window.location.search || '';
    const hash = window.location.hash || '';

    const match = path.match(langSegmentPattern);
    const startsWithLang = path.startsWith('/en'); 
    const currentLang = match?.[1] || (startsWithLang ? 'en' : 'he');
    const targetLang = currentLang === 'en' ? 'he' : 'en';

    let targetPath;

    if (startsWithLang && targetLang === 'he') {
      targetPath = path.replace(/^\/en/, '');
    } else if (match) {
      targetPath = path.replace(langSegmentPattern, `/${targetLang}`);
    } else if (targetLang === 'en') {
      targetPath = path === '/' ? '/en/' : `/en${path}`;
    } else {
      targetPath = path;
    }

    if (!targetPath.startsWith('/')) {
      targetPath = `/${targetPath}`;
    }

    targetPath = targetPath.replace(/\/{2,}/g, '/');

    window.location.href = targetPath + search + hash;
  });
}

function easeOutQuad(t) {
  return t * (2 - t);
}
